/**
 * Deterministic reserved box for uploaded media nodes (Image Upload / Video
 * Upload) — the single source both the node card and the group grid
 * (`computeCellSize`) read, so the size a group reserves for a child before the
 * child mounts equals the size the child renders once it does.
 *
 * Why this exists: with `onlyRenderVisibleElements` on (the default), panning
 * unmounts off-screen nodes and remounts them when they scroll back in, and
 * `canvasImageLoader` defers bitmap admission during the pan gesture. The upload
 * nodes used to keep their decoded size in component state and reserve a 16:9
 * box until the `<img>` fired `onLoad`, so every remount of a portrait upload
 * rendered 315px wide, then snapped to 177/210/236px after the gesture. Each of
 * those width changes altered the group's `childDimensionKey`, re-derived the
 * grid and moved every sibling — the scroll "glitch" and node overlay.
 *
 * Resolution order (first hit wins):
 *   1. `persisted-dimensions` — `metadata.actual_width/actual_height` stored at
 *      upload time. Exact, available before any decode.
 *   2. `decode-cache` — natural dimensions decoded earlier this session, kept in
 *      a module-level cache keyed by the picked asset path (or, while an upload
 *      is still in flight, its local preview URL) so an unmount/remount cannot
 *      lose them. Keying by path rather than by display URL lets the pure group
 *      layout (`computeCellSize`, which has no project id) read the same entry.
 *   3. `persisted-metadata` — `metadata.aspect_ratio`, the canonical
 *      AspectRatio bucket the server snapped the upload to.
 *   4. `schema-default` — the schema's 16:9 layout.
 *
 * Only 1 and 3 are known before a decode; 2 refines legacy uploads at most once
 * per asset per session.
 */

import { ASPECT_RATIO_MAP, ASPECT_WIDTH_MAP, snapNaturalDimensionsToDisplayDimensions } from './constants'

export type ReservedMediaBoxSource =
  | 'persisted-dimensions'
  | 'decode-cache'
  | 'persisted-metadata'
  | 'schema-default'

export interface ReservedMediaBox {
  /** Card width in px (the snapped ASPECT_WIDTH_MAP bucket). */
  width: number
  /** Preview height in px — `width` divided by the rendered aspect ratio, rounded. */
  height: number
  /** CSS `aspect-ratio` value the preview renders with. */
  aspectRatio: string
  /** AspectRatio bucket key the width was snapped to (e.g. `2:3`). */
  ratioKey: string
  source: ReservedMediaBoxSource
}

/** Structural view of `BasicAssetMetadata` — satisfied by both the plain JSON
 *  (`ImageAssetRefDict`) and the generated YMap wrapper (`ImageAssetRefDictYMap`). */
interface PersistedMediaMetadataLike {
  readonly aspect_ratio?: string | null
  readonly actual_width?: number | null
  readonly actual_height?: number | null
}

interface PersistedMediaEntryLike {
  readonly result?: {
    readonly local_path?: string | null
    readonly metadata?: PersistedMediaMetadataLike | null
  } | null
}

export interface PersistedMediaRefLike {
  readonly history?: readonly PersistedMediaEntryLike[] | null
  readonly picked_index?: number | null
}

interface NaturalDimensions {
  width: number
  height: number
}

/** Upper bound on cached assets. A canvas has at most a few thousand media nodes;
 *  the cap only guards against unbounded growth across long sessions. */
const DECODE_CACHE_LIMIT = 4000
const decodedDimensionsByKey = new Map<string, NaturalDimensions>()

/**
 * The project the decode cache currently belongs to. Asset paths are only
 * unique within a project (uploads keep the user's filename, e.g.
 * `images/uploads/photo.png`), so the cache holds one project at a time and is
 * cleared whenever the scope changes. The pure group layout reads the active
 * scope because it has no project id of its own.
 */
let activeProjectScope: string | undefined

function isPositiveFinite(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

/**
 * Point the decode cache at `projectId`, discarding entries from any other
 * project. Idempotent; called by `ProjectIdProvider` (before any canvas node or
 * group layout of that project runs) and by every recorder.
 */
export function setReservedMediaProjectScope(projectId: string | null | undefined): void {
  const next = projectId || undefined
  if (next === activeProjectScope) return
  decodedDimensionsByKey.clear()
  activeProjectScope = next
}

/**
 * Remember the natural dimensions decoded for `key` (see `reservedMediaCacheKey`)
 * in `projectId`. Returns true when the cache changed (the caller should
 * re-render so the new box takes effect).
 */
export function recordDecodedMediaDimensions(
  projectId: string | null | undefined,
  key: string | null | undefined,
  naturalWidth: number,
  naturalHeight: number,
): boolean {
  if (!projectId || !key || !isPositiveFinite(naturalWidth) || !isPositiveFinite(naturalHeight)) return false
  setReservedMediaProjectScope(projectId)
  const existing = decodedDimensionsByKey.get(key)
  if (existing && existing.width === naturalWidth && existing.height === naturalHeight) return false
  if (!existing && decodedDimensionsByKey.size >= DECODE_CACHE_LIMIT) {
    const oldest = decodedDimensionsByKey.keys().next().value
    if (oldest !== undefined) decodedDimensionsByKey.delete(oldest)
  }
  decodedDimensionsByKey.set(key, { width: naturalWidth, height: naturalHeight })
  return true
}

/**
 * Cached natural dimensions for `key`. With `projectId`, entries are returned
 * only while the cache belongs to that project; without it (the group layout),
 * the active scope is trusted.
 */
export function getDecodedMediaDimensions(
  key: string | null | undefined,
  projectId?: string | null,
): NaturalDimensions | undefined {
  if (!key) return undefined
  if (projectId && projectId !== activeProjectScope) return undefined
  return decodedDimensionsByKey.get(key)
}

/** Test-only: reset the module-level decode cache and scope between cases. */
export function clearDecodedMediaDimensionsForTest(): void {
  decodedDimensionsByKey.clear()
  activeProjectScope = undefined
}

function parseRatioKey(ratioKey: string): number {
  const [w, h] = ratioKey.split(':').map(Number)
  return w / h
}

function boxFromNaturalDimensions(
  dims: NaturalDimensions,
  source: ReservedMediaBoxSource,
): ReservedMediaBox {
  const snapped = snapNaturalDimensionsToDisplayDimensions(dims.width, dims.height)
  return {
    width: snapped.width,
    height: Math.round((snapped.width * dims.height) / dims.width),
    aspectRatio: `${dims.width}/${dims.height}`,
    ratioKey: snapped.ratioKey,
    source,
  }
}

function boxFromRatioKey(ratioKey: string, source: ReservedMediaBoxSource): ReservedMediaBox {
  const width = ASPECT_WIDTH_MAP[ratioKey]
  return {
    width,
    height: Math.round(width / parseRatioKey(ratioKey)),
    aspectRatio: ASPECT_RATIO_MAP[ratioKey],
    ratioKey,
    source,
  }
}

function pickedEntry(ref: PersistedMediaRefLike | null | undefined): PersistedMediaEntryLike | undefined {
  const history = ref?.history
  if (!history || history.length === 0) return undefined
  return history[ref?.picked_index ?? 0] ?? undefined
}

/**
 * Decode-cache key for a media node: the picked asset path, or `fallbackKey`
 * (the local preview URL of an in-flight upload) when nothing is persisted yet.
 */
export function reservedMediaCacheKey(
  ref: PersistedMediaRefLike | null | undefined,
  fallbackKey?: string | null,
): string | undefined {
  return pickedEntry(ref)?.result?.local_path || fallbackKey || undefined
}

/** The 16:9 box an upload node reserves when nothing is known about its media. */
export const DEFAULT_RESERVED_MEDIA_BOX: ReservedMediaBox = boxFromRatioKey('16:9', 'schema-default')

/**
 * Resolve the box an uploaded media node reserves, from persisted metadata and
 * the session decode cache. Pure apart from reading the decode cache.
 *
 * @param ref          The node's persisted asset ref (`out_image` / `out_video`).
 * @param fallbackKey  Decode-cache key used only when `ref` has no picked path
 *                     (the preview URL of an upload still in flight).
 * @param projectId    The node's project. When given, decode-cache entries are
 *                     used only if the cache belongs to this project. The group
 *                     layout omits it and relies on the active scope.
 */
export function resolveReservedMediaBox(
  ref: PersistedMediaRefLike | null | undefined,
  fallbackKey?: string | null,
  projectId?: string | null,
): ReservedMediaBox {
  const metadata = pickedEntry(ref)?.result?.metadata ?? undefined
  if (metadata && isPositiveFinite(metadata.actual_width) && isPositiveFinite(metadata.actual_height)) {
    return boxFromNaturalDimensions(
      { width: metadata.actual_width, height: metadata.actual_height },
      'persisted-dimensions',
    )
  }
  const decoded = getDecodedMediaDimensions(reservedMediaCacheKey(ref, fallbackKey), projectId)
  if (decoded) return boxFromNaturalDimensions(decoded, 'decode-cache')
  const ratioKey = metadata?.aspect_ratio
  if (ratioKey && ratioKey in ASPECT_WIDTH_MAP && ratioKey in ASPECT_RATIO_MAP) {
    return boxFromRatioKey(ratioKey, 'persisted-metadata')
  }
  return DEFAULT_RESERVED_MEDIA_BOX
}
